import { useEffect, useState } from 'react'
import { pingsApi } from '../api'
import type { RegionalUptimeStatsResponse, PingRegion } from '../types'
import { PING_REGIONS } from '../types'
import { Card, Skeleton } from '.'
import { Globe, CheckCircle, XCircle, Clock } from 'lucide-react'

interface RegionalStatusMapProps {
  appId: string
  hours?: number
}

// Region positions for the visual display (approximate positions on a simplified world map)
const REGION_POSITIONS: Record<PingRegion, { x: string; y: string; label: string }> = {
  us_east: { x: '25%', y: '35%', label: 'US East' },
  us_west: { x: '12%', y: '35%', label: 'US West' },
  eu_west: { x: '48%', y: '28%', label: 'EU West' },
  asia_pacific: { x: '78%', y: '45%', label: 'Asia Pacific' },
}

export default function RegionalStatusMap({ appId, hours = 24 }: RegionalStatusMapProps) {
  const [stats, setStats] = useState<RegionalUptimeStatsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadStats = async () => {
      try {
        setIsLoading(true)
        const data = await pingsApi.getRegionalStats(appId, hours)
        setStats(data)
        setError(null)
      } catch (err) {
        console.error('Failed to load regional stats:', err)
        setError('Failed to load regional stats')
      } finally {
        setIsLoading(false)
      }
    }
    loadStats()
  }, [appId, hours])

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-slate-500" />
          <h3 className="text-lg font-semibold text-slate-900">Regional Status</h3>
        </div>
        <Skeleton className="h-48 rounded-lg" />
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-slate-500" />
          <h3 className="text-lg font-semibold text-slate-900">Regional Status</h3>
        </div>
        <p className="text-slate-500 text-sm">{error}</p>
      </Card>
    )
  }

  const hasRegionalData = stats && Object.keys(stats.regions).length > 0

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="w-5 h-5 text-slate-500" />
        <h3 className="text-lg font-semibold text-slate-900">Regional Status</h3>
        <span className="text-xs text-slate-500 ml-auto">Last {hours}h</span>
      </div>

      {!hasRegionalData ? (
        <div className="relative bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
          {/* Faint grid background */}
          <svg
            className="absolute inset-0 w-full h-full opacity-10"
            viewBox="0 0 360 180"
            preserveAspectRatio="xMidYMid slice"
          >
            {[30, 60, 90, 120, 150].map(y => (
              <line
                key={`lat-${y}`}
                x1="0"
                y1={y}
                x2="360"
                y2={y}
                stroke="#94a3b8"
                strokeWidth="0.5"
              />
            ))}
            {[60, 120, 180, 240, 300].map(x => (
              <line
                key={`lng-${x}`}
                x1={x}
                y1="0"
                x2={x}
                y2="180"
                stroke="#94a3b8"
                strokeWidth="0.5"
              />
            ))}
          </svg>
          <div className="relative text-center py-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-200 mb-4">
              <Globe className="w-7 h-7 text-slate-500" />
            </div>
            <p className="text-slate-700 text-sm font-medium">No regional data available yet</p>
            <p className="text-slate-500 text-xs mt-1.5 max-w-xs mx-auto">
              Regional monitoring data will appear here once pings are recorded from different
              locations
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Visual world map with region markers */}
          <div className="relative bg-slate-100 rounded-lg h-48 mb-4 overflow-hidden border border-slate-200">
            {/* World map with simplified but recognizable continent shapes */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 50"
              preserveAspectRatio="xMidYMid slice"
            >
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#cbd5e1" strokeWidth="0.1" />
                </pattern>
              </defs>
              {/* Subtle grid background */}
              <rect width="100" height="50" fill="url(#grid)" opacity="0.5" />
              {/* Continent shapes - simplified for clarity */}
              <g fill="#cbd5e1" stroke="#94a3b8" strokeWidth="0.2">
                {/* North America */}
                <path d="M5,8 Q8,6 12,7 L18,6 Q24,5 28,8 L30,12 Q31,16 28,20 L24,22 Q20,24 16,22 L12,24 Q8,22 6,18 L4,14 Q3,10 5,8 Z" />
                {/* Central America */}
                <path d="M16,22 Q18,24 20,26 L18,28 Q16,26 16,22 Z" />
                {/* South America */}
                <path d="M18,28 Q22,27 24,30 L26,36 Q25,42 22,46 L18,48 Q14,46 16,40 L17,34 Q16,30 18,28 Z" />
                {/* Greenland */}
                <path d="M32,4 Q36,3 38,5 L38,8 Q36,10 32,9 L30,6 Q30,4 32,4 Z" />
                {/* UK */}
                <path d="M42,10 Q43,9 44,10 L44,12 Q43,13 42,12 Z" />
                {/* Europe */}
                <path d="M44,8 Q50,6 56,8 L58,12 Q56,16 52,16 L48,14 Q44,12 44,8 Z" />
                {/* Africa */}
                <path d="M46,18 Q52,16 56,20 L58,28 Q56,38 52,42 L46,40 Q42,36 44,28 L46,22 Q44,20 46,18 Z" />
                {/* Middle East */}
                <path d="M58,16 Q62,14 64,18 L62,22 Q58,22 58,16 Z" />
                {/* Russia/Asia */}
                <path d="M58,6 Q68,4 80,6 L90,10 Q94,14 92,18 L86,20 Q78,22 70,20 L62,18 Q56,14 58,6 Z" />
                {/* India */}
                <path d="M68,20 Q72,18 74,22 L72,30 Q68,32 66,28 L68,22 Z" />
                {/* Southeast Asia */}
                <path d="M76,22 Q82,20 86,24 L84,30 Q78,32 76,28 L76,22 Z" />
                {/* Japan */}
                <path d="M90,12 Q92,10 94,12 L94,18 Q92,20 90,18 Z" />
                {/* Australia */}
                <path d="M80,36 Q88,34 92,38 L94,44 Q90,48 84,48 L78,44 Q76,40 80,36 Z" />
                {/* New Zealand */}
                <path d="M96,44 Q98,42 98,46 L96,48 Q94,46 96,44 Z" />
              </g>
            </svg>

            {/* Region markers */}
            {PING_REGIONS.map(({ value, label }) => {
              const regionData = stats.regions[value]
              const position = REGION_POSITIONS[value]

              if (!regionData) {
                return (
                  <div
                    key={value}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
                    style={{ left: position.x, top: position.y }}
                  >
                    <div className="w-3 h-3 rounded-full bg-slate-300 border-2 border-slate-400 shadow-sm" />
                    <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                      <span className="text-[10px] text-slate-500">{label}</span>
                    </div>
                  </div>
                )
              }

              const isHealthy = regionData.current_status === true
              const isDown = regionData.current_status === false

              return (
                <div
                  key={value}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                  style={{ left: position.x, top: position.y }}
                >
                  {/* Outer glow ring */}
                  <div
                    className={`absolute w-8 h-8 rounded-full -inset-2.5 ${
                      isHealthy ? 'bg-green-500' : isDown ? 'bg-red-500' : 'bg-slate-400'
                    } opacity-30 blur-sm`}
                  />
                  {/* Pulse animation for active regions */}
                  <div
                    className={`absolute w-6 h-6 rounded-full -inset-1.5 animate-ping ${
                      isHealthy ? 'bg-green-500' : isDown ? 'bg-red-500' : 'bg-slate-400'
                    } opacity-30`}
                  />
                  {/* Main dot */}
                  <div
                    className={`relative w-3.5 h-3.5 rounded-full border-2 shadow-md ${
                      isHealthy
                        ? 'bg-green-500 border-white shadow-green-600/40'
                        : isDown
                          ? 'bg-red-500 border-white shadow-red-600/40'
                          : 'bg-slate-400 border-white shadow-slate-500/40'
                    }`}
                  />
                  {/* Label */}
                  <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                    <span
                      className={`text-[10px] font-semibold ${
                        isHealthy ? 'text-green-700' : isDown ? 'text-red-700' : 'text-slate-600'
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {/* Hover tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 hidden group-hover:block z-10">
                    <div className="bg-white text-slate-900 text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-xl border border-slate-200">
                      <div className="font-semibold mb-1.5">{label}</div>
                      <div className="text-slate-600 space-y-0.5">
                        <div className="flex justify-between gap-4">
                          <span>Uptime:</span>
                          <span
                            className={`font-medium ${isHealthy ? 'text-green-600' : isDown ? 'text-red-600' : 'text-slate-600'}`}
                          >
                            {regionData.uptime_percentage != null
                              ? `${regionData.uptime_percentage.toFixed(1)}%`
                              : '-'}
                          </span>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span>Avg:</span>
                          <span className="font-medium">
                            {regionData.avg_response_time_ms != null
                              ? `${regionData.avg_response_time_ms}ms`
                              : '-'}
                          </span>
                        </div>
                      </div>
                      {/* Tooltip arrow */}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white" />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Region stats grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PING_REGIONS.map(({ value, label }) => {
              const regionData = stats.regions[value]

              if (!regionData) {
                return (
                  <div
                    key={value}
                    className="bg-slate-50 rounded-lg p-3 border border-slate-200 opacity-50"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium text-slate-600">{label}</span>
                    </div>
                    <p className="text-xs text-slate-400">No data</p>
                  </div>
                )
              }

              const isHealthy = regionData.current_status === true
              const isDown = regionData.current_status === false

              return (
                <div
                  key={value}
                  className={`rounded-lg p-3 border ${
                    isHealthy
                      ? 'bg-green-50 border-green-200'
                      : isDown
                        ? 'bg-red-50 border-red-200'
                        : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {isHealthy ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : isDown ? (
                      <XCircle className="w-4 h-4 text-red-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-slate-500" />
                    )}
                    <span
                      className={`text-sm font-medium ${
                        isHealthy ? 'text-green-800' : isDown ? 'text-red-800' : 'text-slate-700'
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Uptime</span>
                      <span
                        className={`text-xs font-medium ${
                          isHealthy ? 'text-green-700' : isDown ? 'text-red-700' : 'text-slate-700'
                        }`}
                      >
                        {regionData.uptime_percentage != null
                          ? `${regionData.uptime_percentage.toFixed(1)}%`
                          : '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Avg Response</span>
                      <span className="text-xs text-slate-700">
                        {regionData.avg_response_time_ms != null
                          ? `${regionData.avg_response_time_ms}ms`
                          : '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Pings</span>
                      <span className="text-xs text-slate-700">{regionData.total_pings}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </Card>
  )
}
